import { useState } from "react";
import { LineChart, Line, BarChart, Bar, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from "recharts";

const COLORS = {
  bg: "#0a0f1a",
  card: "#111827",
  cardHover: "#1a2236",
  border: "#1e293b",
  accent: "#22d3ee",
  accentDark: "#0891b2",
  green: "#34d399",
  greenDark: "#059669",
  red: "#f87171",
  redDark: "#dc2626",
  orange: "#fb923c",
  yellow: "#fbbf24",
  purple: "#a78bfa",
  pink: "#f472b6",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

const PIE_COLORS = ["#22d3ee", "#34d399", "#a78bfa", "#fb923c", "#f472b6", "#fbbf24", "#818cf8", "#f87171"];

const formatEur = (v) => {
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M€`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K€`;
  return `${v.toFixed(0)}€`;
};

const formatPct = (v) => `${v.toFixed(1)}%`;

const KPI = ({ label, value, sub, color = COLORS.accent, trend, icon }) => (
  <div style={{
    background: `linear-gradient(135deg, ${COLORS.card} 0%, ${COLORS.cardHover} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    padding: "20px 24px",
    position: "relative",
    overflow: "hidden",
  }}>
    <div style={{
      position: "absolute", top: 0, left: 0, width: 4, height: "100%",
      background: `linear-gradient(180deg, ${color}, transparent)`,
    }} />
    <div style={{ fontSize: 12, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8, fontWeight: 600 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.text, fontFamily: "'DM Sans', sans-serif" }}>
      {value}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
      {trend !== undefined && (
        <span style={{
          fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
          background: trend >= 0 ? "rgba(52,211,153,0.15)" : "rgba(248,113,113,0.15)",
          color: trend >= 0 ? COLORS.green : COLORS.red,
        }}>
          {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}%
        </span>
      )}
      <span style={{ fontSize: 12, color: COLORS.textDim }}>{sub}</span>
    </div>
  </div>
);

const Section = ({ title, subtitle, children }) => (
  <div style={{ marginBottom: 40 }}>
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
        {title}
      </h2>
      {subtitle && <p style={{ fontSize: 13, color: COLORS.textDim, margin: "4px 0 0" }}>{subtitle}</p>}
    </div>
    {children}
  </div>
);

const ChartCard = ({ children, title, height = 320 }) => (
  <div style={{
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    padding: "20px 20px 12px",
  }}>
    {title && <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>{title}</div>}
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  </div>
);

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload) return null;
  return (
    <div style={{
      background: "rgba(17,24,39,0.95)", border: `1px solid ${COLORS.border}`,
      borderRadius: 10, padding: "10px 14px", backdropFilter: "blur(8px)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
          <span style={{ fontSize: 12, color: COLORS.textDim }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
            {formatter ? formatter(p.value, p.name) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const revenueData = [
  { year: "2020", total: 1121087, active: 610, arpu: 1838, median: 648, growth: null },
  { year: "2021", total: 1402603, active: 634, arpu: 2212, median: 816, growth: 25.1 },
  { year: "2022", total: 1797039, active: 692, arpu: 2597, median: 891, growth: 28.1 },
  { year: "2023", total: 2369610, active: 738, arpu: 3211, median: 1271, growth: 31.9 },
  { year: "2024", total: 3198112, active: 787, arpu: 4064, median: 1638, growth: 35.0 },
  { year: "2025", total: 4315586, active: 914, arpu: 4722, median: 1762, growth: 34.9 },
  { year: "2026P", total: 5597333, active: 963, arpu: 5812, median: 2150, growth: 29.7, projected: true },
];

const churnData = [
  { year: "2021", churned: 42, new: 66, reactivated: 0, churnRate: 6.9, netNew: 24 },
  { year: "2022", churned: 28, new: 83, reactivated: 3, churnRate: 4.4, netNew: 58 },
  { year: "2023", churned: 32, new: 77, reactivated: 1, churnRate: 4.6, netNew: 46 },
  { year: "2024", churned: 36, new: 84, reactivated: 1, churnRate: 4.9, netNew: 49 },
  { year: "2025", churned: 36, new: 162, reactivated: 1, churnRate: 4.6, netNew: 127 },
  { year: "2026P", churned: 42, new: 91, reactivated: 0, churnRate: 4.7, netNew: 49, projected: true },
];

const retentionData = [
  { year: "2021", nrr: 122.4, grr: 95.0 },
  { year: "2022", nrr: 123.7, grr: 95.4 },
  { year: "2023", nrr: 128.0, grr: 96.0 },
  { year: "2024", nrr: 130.3, grr: 96.1 },
  { year: "2025", nrr: 130.4, grr: 95.7 },
];

const cohortData = [
  { year: "2020", pct: 100 },
  { year: "2021", pct: 93.1 },
  { year: "2022", pct: 89.5 },
  { year: "2023", pct: 86.1 },
  { year: "2024", pct: 82.5 },
  { year: "2025", pct: 79.5 },
];

const accountHealth = [
  { year: "2021", grew: 412, shrunk: 154, grewPct: 72.5, shrunkPct: 27.1 },
  { year: "2022", grew: 440, shrunk: 164, grewPct: 72.6, shrunkPct: 27.1 },
  { year: "2023", grew: 497, shrunk: 161, grewPct: 75.3, shrunkPct: 24.4 },
  { year: "2024", grew: 537, shrunk: 165, grewPct: 76.5, shrunkPct: 23.5 },
  { year: "2025", grew: 584, shrunk: 167, grewPct: 77.8, shrunkPct: 22.2 },
];

const bandData = [
  { name: "0-500€", count: 239, total: 54528, pctCount: 26.1, pctRev: 1.3 },
  { name: "500-1K€", count: 101, total: 75452, pctCount: 11.0, pctRev: 1.7 },
  { name: "1K-2.5K€", count: 185, total: 307133, pctCount: 20.2, pctRev: 7.1 },
  { name: "2.5K-5K€", count: 140, total: 498144, pctCount: 15.3, pctRev: 11.5 },
  { name: "5K-10K€", count: 131, total: 939011, pctCount: 14.3, pctRev: 21.8 },
  { name: "10K-25K€", count: 97, total: 1442855, pctCount: 10.6, pctRev: 33.4 },
  { name: "25K-50K€", count: 16, total: 554015, pctCount: 1.8, pctRev: 12.8 },
  { name: "50K+€", count: 5, total: 444450, pctCount: 0.5, pctRev: 10.3 },
];

const concentrationData = [
  { metric: "Top 5", y2020: 16.4, y2025: 10.3 },
  { metric: "Top 10", y2020: 22.2, y2025: 15.0 },
  { metric: "Top 20", y2020: 31.0, y2025: 22.5 },
  { metric: "Top 50", y2020: 48.8, y2025: 36.1 },
];

const revenueSourceData = [
  { year: "2021", existing: 97.9, new: 2.1, existingAbs: 1372460, newAbs: 30143 },
  { year: "2022", existing: 96.6, new: 3.4, existingAbs: 1735523, newAbs: 61516 },
  { year: "2023", existing: 97.1, new: 2.9, existingAbs: 2300648, newAbs: 68962 },
  { year: "2024", existing: 96.5, new: 3.5, existingAbs: 3087248, newAbs: 110865 },
  { year: "2025", existing: 96.6, new: 3.4, existingAbs: 4169889, newAbs: 145698 },
];

const top10 = [
  { name: "BROKALIA", y2020: 30626, y2025: 119463, cagr: 31.3 },
  { name: "CIUDADELA PROPERTY", y2020: 3532, y2025: 111221, cagr: 99.7 },
  { name: "REBLONE SOLUCIONES", y2020: 90877, y2025: 89653, cagr: -0.3 },
  { name: "TORROELLA MARTÍNEZ", y2020: 33488, y2025: 72574, cagr: 16.7 },
  { name: "ADFINTAL", y2020: 13578, y2025: 51539, cagr: 30.6 },
  { name: "ADM. ARBOLEDA", y2020: 8542, y2025: 44720, cagr: 39.3 },
  { name: "GEFRA ASESORES", y2020: 0, y2025: 43914, cagr: null },
  { name: "CREFINCAS", y2020: 8351, y2025: 41316, cagr: 37.6 },
  { name: "RCT Y ASOCIADOS", y2020: 0, y2025: 36616, cagr: null },
  { name: "OFIGEST INTEGRA", y2020: 692, y2025: 36580, cagr: 120.5 },
];

const tabs = [
  { id: "overview", label: "Resumen Ejecutivo" },
  { id: "revenue", label: "Ingresos" },
  { id: "churn", label: "Churn & Retención" },
  { id: "segments", label: "Segmentación" },
  { id: "projection", label: "Proyección 2026" },
];

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div style={{
      background: COLORS.bg,
      minHeight: "100vh",
      color: COLORS.text,
      fontFamily: "'DM Sans', -apple-system, sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.bg} 0%, #0f172a 50%, #0c1222 100%)`,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "28px 32px 0",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.accent, textTransform: "uppercase", letterSpacing: 3, fontWeight: 700, marginBottom: 6 }}>
              Brokalia · Business Intelligence
            </div>
            <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0, background: `linear-gradient(135deg, ${COLORS.text}, ${COLORS.accent})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Evolución de Colaboradores 2020–2025
            </h1>
            <p style={{ fontSize: 14, color: COLORS.textDim, marginTop: 6 }}>
              Análisis detallado de ingresos, churn y proyección · {new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: COLORS.green }}>4.3M€</div>
            <div style={{ fontSize: 12, color: COLORS.textDim }}>Ingresos 2025</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "none" }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                background: activeTab === t.id ? COLORS.card : "transparent",
                color: activeTab === t.id ? COLORS.accent : COLORS.textDim,
                border: activeTab === t.id ? `1px solid ${COLORS.border}` : "1px solid transparent",
                borderBottom: activeTab === t.id ? `1px solid ${COLORS.card}` : "none",
                borderRadius: "10px 10px 0 0",
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
                fontFamily: "inherit",
                position: "relative",
                bottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto" }}>

        {/* ======== OVERVIEW ======== */}
        {activeTab === "overview" && (
          <>
            <Section title="KPIs Principales" subtitle="Métricas clave del cierre 2025 vs 2024">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
                <KPI label="Ingresos Totales" value="4.32M€" sub="vs 3.20M€ en 2024" trend={34.9} color={COLORS.accent} icon="💰" />
                <KPI label="Colaboradores Activos" value="914" sub="vs 787 en 2024" trend={16.1} color={COLORS.green} icon="👥" />
                <KPI label="ARPU" value="4.722€" sub="vs 4.064€ en 2024" trend={16.2} color={COLORS.purple} icon="📊" />
                <KPI label="Tasa de Churn" value="4,6%" sub="vs 4,9% en 2024" trend={-6.1} color={COLORS.orange} icon="🔄" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                <KPI label="NRR" value="130,4%" sub="Net Revenue Retention" color={COLORS.green} icon="📈" />
                <KPI label="GRR" value="95,7%" sub="Gross Revenue Retention" color={COLORS.accent} icon="🛡️" />
                <KPI label="CAGR 5 Años" value="30,9%" sub="Crecimiento anual compuesto" color={COLORS.yellow} icon="🚀" />
                <KPI label="Nuevos 2025" value="162" sub="Récord histórico captación" trend={92.9} color={COLORS.pink} icon="🆕" />
              </div>
            </Section>

            <Section title="Evolución Histórica" subtitle="Ingresos totales y colaboradores activos 2020–2025">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <ChartCard title="Ingresos Totales (€)">
                  <BarChart data={revenueData.filter(d => !d.projected)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                    <YAxis stroke={COLORS.textDim} fontSize={11} tickFormatter={formatEur} />
                    <Tooltip content={<CustomTooltip formatter={(v) => formatEur(v)} />} />
                    <Bar dataKey="total" name="Ingresos" radius={[6, 6, 0, 0]}>
                      {revenueData.filter(d => !d.projected).map((_, i) => (
                        <Cell key={i} fill={`rgba(34,211,238,${0.4 + i * 0.12})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartCard>
                <ChartCard title="Colaboradores Activos">
                  <ComposedChart data={revenueData.filter(d => !d.projected)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                    <YAxis stroke={COLORS.textDim} fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="active" name="Activos" fill="rgba(52,211,153,0.15)" stroke={COLORS.green} strokeWidth={2} />
                    <Line type="monotone" dataKey="active" name="Activos" stroke={COLORS.green} strokeWidth={3} dot={{ r: 5, fill: COLORS.green }} />
                  </ComposedChart>
                </ChartCard>
              </div>
            </Section>

            <Section title="Hallazgos Clave">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[
                  { title: "Crecimiento acelerado sostenido", text: "Los ingresos han crecido un 285% en 5 años (CAGR 30,9%), con aceleración YoY pasando del 25% en 2021 al 35% en 2024-2025. El motor principal es la expansión de cuentas existentes (NRR >130%).", color: COLORS.green },
                  { title: "Churn bajo y estable", text: "La tasa de churn se ha estabilizado en ~4,5-4,9% anual. La retención de la cohorte 2020 tras 5 años es del 79,5%, equivalente a un churn compuesto muy contenido para el sector.", color: COLORS.accent },
                  { title: "2025: año récord de captación", text: "Se incorporaron 162 nuevos colaboradores, casi el doble que el promedio anterior (78/año). Esto, junto al bajo churn, generó un salto neto de +127 colaboradores activos.", color: COLORS.yellow },
                  { title: "Desconcentración saludable", text: "El peso del Top 10 pasó del 22,2% al 15,0% de los ingresos. La base se diversifica progresivamente, reduciendo el riesgo de dependencia de grandes cuentas.", color: COLORS.purple },
                ].map((item, i) => (
                  <div key={i} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "18px 22px", borderLeft: `4px solid ${item.color}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: item.color, marginBottom: 8 }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>{item.text}</div>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* ======== REVENUE TAB ======== */}
        {activeTab === "revenue" && (
          <>
            <Section title="Crecimiento de Ingresos" subtitle="Evolución interanual del volumen total y crecimiento">
              <ChartCard title="Ingresos Totales & Crecimiento YoY" height={350}>
                <ComposedChart data={revenueData.filter(d => !d.projected)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis yAxisId="left" stroke={COLORS.textDim} fontSize={11} tickFormatter={formatEur} />
                  <YAxis yAxisId="right" orientation="right" stroke={COLORS.orange} fontSize={11} tickFormatter={v => `${v}%`} />
                  <Tooltip content={<CustomTooltip formatter={(v, n) => n.includes("Crecimiento") ? `${v}%` : formatEur(v)} />} />
                  <Bar yAxisId="left" dataKey="total" name="Ingresos" radius={[6, 6, 0, 0]} fill={COLORS.accent} fillOpacity={0.7} />
                  <Line yAxisId="right" type="monotone" dataKey="growth" name="Crecimiento YoY" stroke={COLORS.orange} strokeWidth={3} dot={{ r: 5, fill: COLORS.orange }} connectNulls />
                </ComposedChart>
              </ChartCard>
            </Section>

            <Section title="Ingreso Medio por Colaborador (ARPU)" subtitle="Media y mediana de ingresos por cuenta activa">
              <ChartCard title="ARPU vs Mediana (€)" height={320}>
                <LineChart data={revenueData.filter(d => !d.projected)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis stroke={COLORS.textDim} fontSize={11} tickFormatter={formatEur} />
                  <Tooltip content={<CustomTooltip formatter={(v) => formatEur(v)} />} />
                  <Legend />
                  <Line type="monotone" dataKey="arpu" name="ARPU (Media)" stroke={COLORS.accent} strokeWidth={3} dot={{ r: 5 }} />
                  <Line type="monotone" dataKey="median" name="Mediana" stroke={COLORS.purple} strokeWidth={3} dot={{ r: 5 }} strokeDasharray="5 5" />
                </LineChart>
              </ChartCard>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 22px", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
                  <strong style={{ color: COLORS.accent }}>Análisis ARPU:</strong> El ARPU medio ha crecido de 1.838€ a 4.722€ (+157% en 5 años). Sin embargo, la brecha creciente entre media (4.722€) y mediana (1.762€) indica una distribución asimétrica: las grandes cuentas elevan significativamente la media. El crecimiento de la mediana (+172%) confirma que también las cuentas medianas están expandiéndose de forma saludable.
                </div>
              </div>
            </Section>

            <Section title="Origen de los Ingresos" subtitle="Contribución de cuentas existentes vs nuevas captaciones">
              <ChartCard title="Ingresos: Existentes vs Nuevos (€)" height={320}>
                <BarChart data={revenueSourceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis stroke={COLORS.textDim} fontSize={11} tickFormatter={formatEur} />
                  <Tooltip content={<CustomTooltip formatter={(v) => formatEur(v)} />} />
                  <Legend />
                  <Bar dataKey="existingAbs" name="Existentes" stackId="a" fill={COLORS.accent} fillOpacity={0.7} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="newAbs" name="Nuevos" stackId="a" fill={COLORS.orange} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ChartCard>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 22px", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
                  <strong style={{ color: COLORS.orange }}>Nota:</strong> Los nuevos colaboradores generan ~3,4% de los ingresos en su primer año. El verdadero valor de la captación se materializa en años posteriores cuando las cuentas maduran. Esto refuerza la importancia de la retención como principal palanca de crecimiento.
                </div>
              </div>
            </Section>

            <Section title="Top 10 Colaboradores 2025">
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: COLORS.cardHover }}>
                      {["#", "Colaborador", "2020", "2025", "CAGR"].map(h => (
                        <th key={h} style={{ padding: "12px 16px", textAlign: h === "Colaborador" ? "left" : "right", color: COLORS.textDim, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {top10.map((row, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: "10px 16px", textAlign: "right", color: COLORS.textDim }}>{i + 1}</td>
                        <td style={{ padding: "10px 16px", fontWeight: 600, color: COLORS.text }}>{row.name}</td>
                        <td style={{ padding: "10px 16px", textAlign: "right", color: COLORS.textMuted }}>{row.y2020 > 0 ? formatEur(row.y2020) : "—"}</td>
                        <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: COLORS.accent }}>{formatEur(row.y2025)}</td>
                        <td style={{ padding: "10px 16px", textAlign: "right", color: row.cagr === null ? COLORS.textDim : row.cagr > 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}>
                          {row.cagr === null ? "Nuevo" : `${row.cagr.toFixed(1)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}

        {/* ======== CHURN TAB ======== */}
        {activeTab === "churn" && (
          <>
            <Section title="Análisis de Churn" subtitle="Pérdida de colaboradores y dinámica de la base">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                <KPI label="Churn Rate Medio" value="5,1%" sub="Media 2021-2025" color={COLORS.red} icon="📉" />
                <KPI label="Bajas Totales 5 Años" value="174" sub="de 610 iniciales en 2020" color={COLORS.orange} icon="🚪" />
                <KPI label="Retención Cohorte 2020" value="79,5%" sub="485 de 610 siguen activos" color={COLORS.green} icon="🏆" />
              </div>
              <ChartCard title="Altas vs Bajas de Colaboradores" height={320}>
                <ComposedChart data={churnData.filter(d => !d.projected)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis yAxisId="left" stroke={COLORS.textDim} fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke={COLORS.red} fontSize={11} tickFormatter={v => `${v}%`} />
                  <Tooltip content={<CustomTooltip formatter={(v, n) => n.includes("Rate") ? `${v}%` : v} />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="new" name="Nuevos" fill={COLORS.green} fillOpacity={0.8} radius={[6, 6, 0, 0]} />
                  <Bar yAxisId="left" dataKey="churned" name="Bajas" fill={COLORS.red} fillOpacity={0.7} radius={[6, 6, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="churnRate" name="Churn Rate" stroke={COLORS.yellow} strokeWidth={3} dot={{ r: 5, fill: COLORS.yellow }} />
                </ComposedChart>
              </ChartCard>
            </Section>

            <Section title="Retención de Ingresos" subtitle="NRR y GRR — métricas SaaS aplicadas a seguros">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <ChartCard title="Net Revenue Retention (NRR)" height={280}>
                  <LineChart data={retentionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                    <YAxis domain={[100, 140]} stroke={COLORS.textDim} fontSize={11} tickFormatter={v => `${v}%`} />
                    <ReferenceLine y={100} stroke={COLORS.textDim} strokeDasharray="3 3" label={{ value: "100%", fill: COLORS.textDim, fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
                    <Line type="monotone" dataKey="nrr" name="NRR" stroke={COLORS.green} strokeWidth={3} dot={{ r: 5, fill: COLORS.green }} />
                  </LineChart>
                </ChartCard>
                <ChartCard title="Gross Revenue Retention (GRR)" height={280}>
                  <LineChart data={retentionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                    <YAxis domain={[90, 100]} stroke={COLORS.textDim} fontSize={11} tickFormatter={v => `${v}%`} />
                    <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
                    <Line type="monotone" dataKey="grr" name="GRR" stroke={COLORS.accent} strokeWidth={3} dot={{ r: 5, fill: COLORS.accent }} />
                  </LineChart>
                </ChartCard>
              </div>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 22px", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
                  <strong style={{ color: COLORS.green }}>NRR &gt;130%:</strong> Cada euro de base instalada genera 1,30€ al año siguiente gracias a la expansión orgánica de cuentas. Esto sitúa a Brokalia al nivel de las mejores empresas SaaS en retención. El <strong style={{ color: COLORS.accent }}>GRR ~96%</strong> confirma que las bajas de ingresos por churn y contracción son mínimas, solo un 4% anual.
                </div>
              </div>
            </Section>

            <Section title="Supervivencia de la Cohorte 2020" subtitle="De los 610 colaboradores activos en 2020, ¿cuántos siguen?">
              <ChartCard title="% Colaboradores Cohorte 2020 Activos" height={280}>
                <ComposedChart data={cohortData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis domain={[70, 100]} stroke={COLORS.textDim} fontSize={11} tickFormatter={v => `${v}%`} />
                  <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
                  <Area type="monotone" dataKey="pct" name="% Activos" fill="rgba(34,211,238,0.15)" stroke={COLORS.accent} strokeWidth={2} />
                  <Line type="monotone" dataKey="pct" name="% Activos" stroke={COLORS.accent} strokeWidth={3} dot={{ r: 5, fill: COLORS.accent }} />
                </ComposedChart>
              </ChartCard>
            </Section>

            <Section title="Salud de Cuentas Retenidas" subtitle="De las cuentas que se mantienen, ¿cuántas crecen vs decrecen?">
              <ChartCard title="Cuentas que Crecen vs Decrecen (del total retenido)" height={300}>
                <BarChart data={accountHealth}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis stroke={COLORS.textDim} fontSize={11} tickFormatter={v => `${v}%`} />
                  <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
                  <Legend />
                  <Bar dataKey="grewPct" name="% Crecen" fill={COLORS.green} fillOpacity={0.8} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="shrunkPct" name="% Decrecen" fill={COLORS.red} fillOpacity={0.6} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ChartCard>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 22px", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
                  La proporción de cuentas que crecen mejora cada año: del 72,5% en 2021 al 77,8% en 2025. Esto indica un <strong style={{ color: COLORS.green }}>efecto flywheel</strong>: los colaboradores no solo se retienen sino que generan más negocio progresivamente, probablemente por mayor confianza y ampliación de comunidades aseguradas.
                </div>
              </div>
            </Section>
          </>
        )}

        {/* ======== SEGMENTS TAB ======== */}
        {activeTab === "segments" && (
          <>
            <Section title="Distribución por Bandas de Ingreso (2025)" subtitle="Cómo se reparten los 914 colaboradores activos">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <ChartCard title="Nº de Colaboradores por Banda" height={320}>
                  <BarChart data={bandData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis type="number" stroke={COLORS.textDim} fontSize={11} />
                    <YAxis dataKey="name" type="category" stroke={COLORS.textDim} fontSize={11} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Colaboradores" radius={[0, 6, 6, 0]}>
                      {bandData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} fillOpacity={0.8} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartCard>
                <ChartCard title="% de Ingresos por Banda" height={320}>
                  <PieChart>
                    <Pie data={bandData} dataKey="pctRev" nameKey="name" cx="50%" cy="50%" outerRadius={110} innerRadius={55} paddingAngle={2} label={({ name, pctRev }) => pctRev > 5 ? `${name}` : ""} labelLine={false} fontSize={11}>
                      {bandData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ChartCard>
              </div>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 22px", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
                  <strong style={{ color: COLORS.accent }}>Regla 80/20 invertida:</strong> El 12,9% de los colaboradores (118 cuentas de 10K€+) generan el 56,5% de los ingresos. Las 239 cuentas más pequeñas (&lt;500€) representan el 26% de las cuentas pero solo el 1,3% de los ingresos. La banda de 5K-25K€ (228 cuentas, 25%) es el "core" del negocio con el 55,2% de los ingresos.
                </div>
              </div>
            </Section>

            <Section title="Evolución de la Concentración" subtitle="Peso del Top N sobre ingresos totales: 2020 vs 2025">
              <ChartCard title="Concentración de Ingresos (% del total)" height={300}>
                <BarChart data={concentrationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="metric" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis stroke={COLORS.textDim} fontSize={11} tickFormatter={v => `${v}%`} domain={[0, 55]} />
                  <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
                  <Legend />
                  <Bar dataKey="y2020" name="2020" fill={COLORS.purple} fillOpacity={0.6} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="y2025" name="2025" fill={COLORS.accent} fillOpacity={0.8} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ChartCard>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 22px", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
                  <strong style={{ color: COLORS.purple }}>Tendencia positiva:</strong> La concentración ha disminuido en todos los segmentos. El Top 50 ha pasado de controlar el 48,8% al 36,1%. Esto reduce el riesgo operativo y demuestra que el crecimiento se distribuye entre un número mayor de colaboradores. Caso destacado: REBLONE SOLUCIONES pasó de representar el 8,1% del total en 2020 al 2,1% en 2025 (por crecimiento del denominador más que por caída propia).
                </div>
              </div>
            </Section>
          </>
        )}

        {/* ======== PROJECTION TAB ======== */}
        {activeTab === "projection" && (
          <>
            <Section title="Proyección 2026" subtitle="Escenarios basados en tendencias históricas y factores de ajuste">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                {[
                  { label: "Conservador", growth: "25%", rev: "5.39M€", active: "940", color: COLORS.orange, desc: "Desaceleración por base más grande y normalización de captación" },
                  { label: "Base", growth: "29,7%", rev: "5.60M€", active: "963", color: COLORS.accent, desc: "Mantiene NRR ~130% con captación moderada (91 nuevos)" },
                  { label: "Optimista", growth: "35%", rev: "5.83M€", active: "1.010", color: COLORS.green, desc: "Mantiene ritmo 2025 con captación sostenida >120 nuevos" },
                ].map((sc, i) => (
                  <div key={i} style={{ background: COLORS.card, border: `1px solid ${sc.color}40`, borderRadius: 16, padding: "22px 24px", borderTop: `3px solid ${sc.color}` }}>
                    <div style={{ fontSize: 11, color: sc.color, textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>{sc.label}</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: COLORS.text, marginBottom: 4 }}>{sc.rev}</div>
                    <div style={{ fontSize: 14, color: sc.color, fontWeight: 600, marginBottom: 12 }}>+{sc.growth} YoY · {sc.active} activos</div>
                    <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5 }}>{sc.desc}</div>
                  </div>
                ))}
              </div>

              <ChartCard title="Proyección de Ingresos (Escenario Base)" height={350}>
                <ComposedChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="year" stroke={COLORS.textDim} fontSize={12} />
                  <YAxis stroke={COLORS.textDim} fontSize={11} tickFormatter={formatEur} />
                  <Tooltip content={<CustomTooltip formatter={(v) => formatEur(v)} />} />
                  <Bar dataKey="total" name="Ingresos" radius={[6, 6, 0, 0]}>
                    {revenueData.map((d, i) => (
                      <Cell key={i} fill={d.projected ? COLORS.accent : COLORS.accent} fillOpacity={d.projected ? 0.4 : 0.7} stroke={d.projected ? COLORS.accent : "none"} strokeWidth={d.projected ? 2 : 0} strokeDasharray={d.projected ? "6 3" : "none"} />
                    ))}
                  </Bar>
                </ComposedChart>
              </ChartCard>
            </Section>

            <Section title="Metodología de Proyección">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[
                  { title: "Ingresos Base Instalada", items: [
                    "NRR proyectada: ~128-130% (ligera moderación por base más grande)",
                    "GRR estable en ~95-96%",
                    "Ingresos de cuentas existentes: ~5.45M€"
                  ]},
                  { title: "Nuevas Captaciones", items: [
                    "Promedio 3 últimos años: 108 nuevos/año",
                    "2025 fue excepcional (162), se proyecta normalización a ~91",
                    "ARPU primer año estimado: ~1.600€ → aporte ~145K€"
                  ]},
                  { title: "Churn Esperado", items: [
                    "Tasa proyectada: ~4,7% (promedio 3 años)",
                    "Bajas estimadas: 42 colaboradores",
                    "Pérdida de ingresos por churn: ~125-150K€"
                  ]},
                  { title: "Factores de Riesgo", items: [
                    "REBLONE SOLUCIONES muestra tendencia descendente (135K→90K): riesgo de pérdida significativa",
                    "La captación récord de 2025 puede no ser sostenible",
                    "Posible saturación del ARPU en cuentas maduras"
                  ]},
                ].map((block, i) => (
                  <div key={i} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "18px 22px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.accent, marginBottom: 12 }}>{block.title}</div>
                    {block.items.map((item, j) => (
                      <div key={j} style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7, paddingLeft: 16, position: "relative", marginBottom: 4 }}>
                        <span style={{ position: "absolute", left: 0, color: COLORS.textDim }}>→</span>
                        {item}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Métricas Proyectadas 2026 (Escenario Base)">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                <KPI label="Ingresos Totales" value="5.60M€" sub="vs 4.32M€ en 2025" trend={29.7} color={COLORS.accent} icon="💰" />
                <KPI label="Colaboradores Activos" value="963" sub="vs 914 en 2025" trend={5.4} color={COLORS.green} icon="👥" />
                <KPI label="ARPU Esperado" value="5.812€" sub="vs 4.722€ en 2025" trend={23.1} color={COLORS.purple} icon="📊" />
                <KPI label="Churn Proyectado" value="~42" sub="4,7% sobre base 2025" color={COLORS.orange} icon="🔄" />
              </div>
            </Section>

            <Section title="Recomendaciones Estratégicas">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[
                  { n: "01", title: "Proteger las Top 20 cuentas", text: "Representan el 22,5% de los ingresos. Un plan de retención proactivo con seguimiento trimestral podría prevenir pérdidas de alto impacto. Especial atención a REBLONE SOLUCIONES cuya tendencia es decreciente.", color: COLORS.red },
                  { n: "02", title: "Acelerar cuentas de 2.5K-10K€", text: "Son 271 cuentas que generan el 33,3% de ingresos. Identificar las 50 con mayor potencial de expansión y desarrollar planes de crecimiento personalizados podría mover muchas a la banda 10K+.", color: COLORS.accent },
                  { n: "03", title: "Mantener motor de captación", text: "El salto a 162 nuevos en 2025 es un hito. Analizar qué impulsó esta aceleración (¿canal, zona, producto?) y sistematizar el proceso para sostener al menos 100+ nuevos anuales.", color: COLORS.green },
                  { n: "04", title: "Reducir la cola larga improductiva", text: "239 cuentas generan menos de 500€/año (1,3% de ingresos). Evaluar el coste de servicio y considerar estrategias de activación o, si no son viables, redireccionar el esfuerzo comercial.", color: COLORS.yellow },
                ].map((rec, i) => (
                  <div key={i} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "22px 24px", borderLeft: `4px solid ${rec.color}` }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: rec.color, opacity: 0.5, lineHeight: 1 }}>{rec.n}</div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: rec.color, marginBottom: 8 }}>{rec.title}</div>
                        <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>{rec.text}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", padding: "32px 0 16px", borderTop: `1px solid ${COLORS.border}`, marginTop: 32 }}>
          <div style={{ fontSize: 11, color: COLORS.textDim }}>
            Brokalia · Análisis de Evolución de Colaboradores · Datos: 1.083 registros, 2020-2025 · Generado con Claude AI
          </div>
        </div>
      </div>
    </div>
  );
}
